import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Activity } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { useLang } from "../context/LanguageContext";
import api, { formatApiError } from "../lib/api";

const SIDE_IMAGE =
  "https://images.unsplash.com/photo-1526676317768-d9b14f15615a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NjZ8MHwxfHNlYXJjaHwxfHxzcG9ydHMlMjBjb2FjaCUyMGxhYm9yYXRvcnklMjB0ZXN0aW5nJTIwYXRobGV0ZXxlbnwwfHx8fDE3ODgxNzgyMTJ8MA&ixlib=rb-4.1.0&q=85";

export default function Auth({ mode }) {
  const { setUser } = useAuth();
  const { t } = useLang();
  const navigate = useNavigate();
  const isLogin = mode === "login";
  const [params] = useSearchParams();
  const resetDone = isLogin && params.get("reset") === "1";

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const payload = isLogin ? { email, password } : { name, email, password };
      const { data } = await api.post(`/auth/${isLogin ? "login" : "register"}`, payload);
      setUser(data);
      navigate(data.role === "admin" ? "/admin" : "/dashboard");
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-5 sm:px-6 py-8" data-testid="auth-page">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <motion.div
          className="card-dark p-8 sm:p-12 flex items-center"
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <div className="w-full max-w-md mx-auto">
            <span className="badge-lime">SportScience Academy</span>
            <h1 className="font-heading font-black text-3xl sm:text-4xl lg:text-5xl mt-6 text-[#0B1B3A]" data-testid="auth-title">
              {isLogin ? t("auth.login.title") : t("auth.register.title")}
            </h1>
            <p className="text-[#64748B] mt-3">{isLogin ? t("auth.login.subtitle") : t("auth.register.subtitle")}</p>

            <form onSubmit={handleSubmit} className="mt-9 space-y-5" data-testid="auth-form">
              {resetDone && (
                <div data-testid="auth-reset-done" className="bg-[#1D4ED8]/10 border border-[#1D4ED8]/40 rounded-2xl text-[#1D4ED8] text-sm font-semibold px-5 py-3">
                  {t("auth.reset.done")}
                </div>
              )}
              {!isLogin && (
                <div>
                  <label className="text-[11px] font-bold uppercase tracking-[0.22em] text-[#94A3B8]">{t("auth.name")}</label>
                  <input
                    data-testid="auth-name-input"
                    type="text"
                    required
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="input-flat mt-2"
                    placeholder={t("auth.name")}
                  />
                </div>
              )}
              <div>
                <label className="text-[11px] font-bold uppercase tracking-[0.22em] text-[#94A3B8]">{t("auth.email")}</label>
                <input
                  data-testid="auth-email-input"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="input-flat mt-2"
                  placeholder="you@example.com"
                />
              </div>
              <div>
                <label className="text-[11px] font-bold uppercase tracking-[0.22em] text-[#94A3B8]">{t("auth.password")}</label>
                <input
                  data-testid="auth-password-input"
                  type="password"
                  required
                  minLength={6}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="input-flat mt-2"
                  placeholder="••••••••"
                />
              </div>

              {error && (
                <div data-testid="auth-error" className="bg-[#DC2626]/10 border border-[#DC2626]/50 rounded-2xl text-[#DC2626] text-sm font-semibold px-5 py-3">
                  {error}
                </div>
              )}

              <button data-testid="auth-submit-button" type="submit" disabled={loading} className="btn-blue w-full disabled:opacity-50">
                {loading ? t("common.loading") : isLogin ? t("auth.login.submit") : t("auth.register.submit")}
              </button>
              {isLogin && (
                <Link data-testid="auth-forgot-link" to="/forgot-password" className="block text-sm font-bold text-[#1D4ED8] hover:underline text-center">
                  {t("auth.forgot.link")}
                </Link>
              )}
            </form>

            <p className="text-sm text-[#64748B] mt-8">
              {isLogin ? t("auth.noAccount") : t("auth.hasAccount")}{" "}
              <Link data-testid="auth-switch-link" to={isLogin ? "/register" : "/login"} className="font-bold text-[#1D4ED8] hover:underline">
                {isLogin ? t("nav.register") : t("nav.login")}
              </Link>
            </p>
          </div>
        </motion.div>

        <motion.div
          className="hidden lg:block relative rounded-[24px] overflow-hidden bg-white"
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.1 }}
        >
          <img src={SIDE_IMAGE} alt="Athlete on blocks" className="absolute inset-0 w-full h-full object-cover opacity-55" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0B1B3A] via-transparent to-transparent" />
          <div className="absolute bottom-10 left-10 right-10">
            <div className="flex items-center gap-3">
              <span className="w-10 h-10 rounded-full bg-[#1D4ED8] flex items-center justify-center">
                <Activity className="w-5 h-5 text-white" strokeWidth={2.5} />
              </span>
              <span className="font-heading font-black text-xl text-white">SportScience Academy</span>
            </div>
            <p className="font-heading font-black text-2xl text-white mt-5">{t("brand.tagline")}</p>
            <p className="text-white/70 mt-3 max-w-md text-sm">{t("hero.subtitle")}</p>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
